/* USAGE
<FeatureHeaderButton
  show={true}   // what is the difference between show and enabled? can they mean the same thing?
  enabled={true} // if false, grey out and disable the button
  linkTo="/feature" // link to url; if null, render without href or Link
  reactRouterEnabled={true} // if reactRouter is being used, use Link instead of href
  buttonText="button text"
  clickCallback={callbackFunction}
  secondaryStyle={true}
/>

*/

import * as React from "react";

// utilities
import { gaTag } from "@utils/browser-helpers";
import Button from "@components/ui/Button";

const FeatureHeaderButton = ({
  btnId,
  show,
  enabled,
  linkTo,
  reactRouterEnabled,
  buttonText,
  clickCallback,
  customStyle,
  customButtonStyle,
  customGATag,
  theme,
  icon,
  type='submit',
}: {
  btnId: string;
  show: boolean;
  enabled: boolean;
  linkTo?: string | null;
  reactRouterEnabled?: boolean;
  buttonText: string;
  clickCallback: () => void;
  customStyle?: React.CSSProperties;
  customButtonStyle?: React.CSSProperties | string;
  customGATag?: string;
  theme?: "primary" | "secondary" | "tertiary";
  icon?: React.ReactNode;
  type?: "button" | "submit" | "reset";
}) => {
  if (!show) return null;

  if (linkTo && enabled) {
    if (reactRouterEnabled) {
      return (
        <a
          id={btnId}
          data-testid={"BUTTON-" + buttonText}
          style={{ ...customStyle }}
          data-ga_click_id={btnId}
          data-ga_tag={gaTag()}
          href={linkTo}
        >
          <Button
            theme={theme}
            icon={icon as any}
            disabled={!enabled}
            customStyle={customButtonStyle as string}
            ariaLabel={buttonText}
            componentID={btnId}
            label={buttonText}
            customGATag={customGATag}
            onClickCallback={enabled ? clickCallback : null}
          />
        </a>
      );
    } else {
      return (
        <a href={linkTo}>
          <Button
            theme={theme}
            icon={icon as any}
            disabled={!enabled}
            dataTestID={"BUTTON-" + buttonText}
            customStyle={customButtonStyle as string}
            ariaLabel={buttonText}
            componentID={btnId}
            label={buttonText}
            type={type}
            customGATag={customGATag}
            onClickCallback={enabled ? clickCallback : null}
          />
        </a>
      );
    }
  }

  return (
    <Button
      theme={theme}
      icon={icon as any}
      disabled={!enabled}
      customStyle={customButtonStyle as string}
      label={buttonText}
      type={type}
      componentID={btnId}
      ariaLabel={buttonText}
      data-ga_click_id={btnId}
      data-ga_tag={gaTag()}
      onClickCallback={enabled ? clickCallback : null}
    />
  );
};

export default FeatureHeaderButton;
